import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Users, MapPin, Wrench, Mountain, Package, Timer, UserPlus, BarChart3, Bell, Send, FileText, Key, Copy, RefreshCw, Navigation } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Textarea } from "@/components/ui/textarea";

// Config for each lookup tab
const TAB_CONFIG = {
  sites: {
    label: "Sites", icon: MapPin, endpoint: "/admin/sites",
    fields: [
      { key: "name", label: "Name", type: "text", required: true, placeholder: "TX - Gonzales" },
      { key: "state", label: "State", type: "select", required: true, options: ["TX", "CA", "AZ"] },
      { key: "code", label: "Code", type: "text", required: true, placeholder: "TX01" },
    ],
    columns: ["name", "state", "code"],
  },
  contractors: {
    label: "Contractors", icon: Users, endpoint: "/admin/contractors",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "code", label: "Code", type: "text", required: true },
      { key: "contract_type", label: "Contract Type", type: "select", options: ["Hourly", "$/cyd", "Hybrid"] },
    ],
    columns: ["name", "code", "contract_type"],
  },
  equipment: {
    label: "Equipment", icon: Wrench, endpoint: "/admin/equipment",
    fields: [
      { key: "unit_id", label: "Unit ID", type: "text", required: true, placeholder: "CAT336-01" },
      { key: "equipment_type", label: "Type", type: "select", required: true, options: ["Excavator", "Loader", "Dozer", "Grader", "Water Truck", "Haul Truck", "Other"] },
      { key: "make_model", label: "Make/Model", type: "text", placeholder: "CAT 336" },
    ],
    columns: ["unit_id", "equipment_type", "make_model"],
  },
  materials: {
    label: "Materials", icon: Mountain, endpoint: "/admin/materials",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category", label: "Category", type: "select", required: true, options: ["Ore", "Waste", "Other"] },
    ],
    columns: ["name", "category"],
  },
  locations: {
    label: "Locations", icon: MapPin, endpoint: "/admin/locations",
    fields: [
      { key: "name", label: "Name", type: "text", required: true, placeholder: "Pit A" },
      { key: "location_type", label: "Type", type: "select", required: true, options: ["Pit/Bench", "ROM", "Stockpile", "WasteDump", "Other"] },
    ],
    columns: ["name", "location_type"],
  },
  "delay-codes": {
    label: "Delay Codes", icon: Timer, endpoint: "/admin/delay-codes",
    fields: [
      { key: "code", label: "Code", type: "text", required: true, placeholder: "S1" },
      { key: "category", label: "Category", type: "select", required: true, options: ["Standby", "Downtime"] },
      { key: "description", label: "Description", type: "text", required: true },
    ],
    columns: ["code", "category", "description"],
  },
};

function GeofenceDialog({ site, open, onClose, onSaved }) {
  const [geoType, setGeoType] = useState("polygon");
  const [coordsText, setCoordsText] = useState("");
  const [centerLat, setCenterLat] = useState("");
  const [centerLng, setCenterLng] = useState("");
  const [radius, setRadius] = useState("1");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (site?.geofence) {
      const gf = site.geofence;
      setGeoType(gf.type || "polygon");
      if (gf.type === "polygon" && gf.coordinates) {
        setCoordsText(gf.coordinates.map(c => `${c[0]}, ${c[1]}`).join("\n"));
      }
      if (gf.center) {
        setCenterLat(String(gf.center[0] || ""));
        setCenterLng(String(gf.center[1] || ""));
      }
      setRadius(String(gf.radius_km || 1));
    } else {
      setGeoType("polygon");
      setCoordsText("");
      setCenterLat("");
      setCenterLng("");
      setRadius("1");
    }
  }, [site]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload = { type: geoType };
      if (geoType === "polygon") {
        const coords = coordsText.split("\n").filter(l => l.trim()).map(l => {
          const [lat, lng] = l.split(",").map(s => parseFloat(s.trim()));
          if (isNaN(lat) || isNaN(lng)) throw new Error("Invalid coordinate format");
          return [lat, lng];
        });
        if (coords.length < 3) { toast.error("Polygon needs at least 3 coordinate pairs"); setSaving(false); return; }
        payload.coordinates = coords;
      } else {
        if (!centerLat || !centerLng) { toast.error("Center coordinates required"); setSaving(false); return; }
        payload.center = [parseFloat(centerLat), parseFloat(centerLng)];
        payload.radius_km = parseFloat(radius) || 1;
      }
      await api.put(`/admin/sites/${site.id}/geofence`, payload);
      toast.success("Geofence saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save geofence");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete(`/admin/sites/${site.id}/geofence`);
      toast.success("Geofence removed");
      onSaved();
      onClose();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader><DialogTitle className="font-headline">Geofence — {site?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Type</Label>
            <Select value={geoType} onValueChange={setGeoType}>
              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="polygon">Polygon (Perimeter)</SelectItem>
                <SelectItem value="circle">Circle (Center + Radius)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {geoType === "polygon" ? (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Coordinates (one per line: lat, lng)</Label>
              <Textarea
                value={coordsText}
                onChange={e => setCoordsText(e.target.value)}
                placeholder={"29.5710, -97.4437\n29.5730, -97.4410\n29.5715, -97.4385\n29.5690, -97.4420"}
                rows={6}
                className="bg-background border-border font-mono text-xs"
                data-testid="geofence-coords-input"
              />
              <p className="text-xs text-muted-foreground">Enter the mine site perimeter as lat, lng pairs. Minimum 3 points.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Center Latitude</Label>
                  <Input value={centerLat} onChange={e => setCenterLat(e.target.value)} placeholder="29.5710" className="bg-background border-border" data-testid="geofence-center-lat" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Center Longitude</Label>
                  <Input value={centerLng} onChange={e => setCenterLng(e.target.value)} placeholder="-97.4437" className="bg-background border-border" data-testid="geofence-center-lng" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Radius (km)</Label>
                <Input value={radius} onChange={e => setRadius(e.target.value)} placeholder="1" className="bg-background border-border" data-testid="geofence-radius" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          {site?.geofence && (
            <Button variant="outline" size="sm" onClick={handleRemove} className="border-destructive text-destructive mr-auto" data-testid="remove-geofence-button">Remove</Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose} className="border-border">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-geofence-button">{saving ? "Saving..." : "Save Geofence"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LookupTab({ config }) {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [geoSite, setGeoSite] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      const data = await api.get(config.endpoint);
      setItems(data || []);
    } catch (err) {
      toast.error(`Failed to load ${config.label.toLowerCase()}`);
    }
  }, [config]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const openAdd = () => {
    setEditItem(null);
    const initial = {};
    config.fields.forEach(f => { initial[f.key] = ""; });
    setForm(initial);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    const initial = {};
    config.fields.forEach(f => { initial[f.key] = item[f.key] || ""; });
    setForm(initial);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const missing = config.fields.filter(f => f.required && !form[f.key]);
    if (missing.length) { toast.error(`${missing[0].label} is required`); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`${config.endpoint}/${editItem.id}`, form);
        toast.success("Updated");
      } else {
        await api.post(config.endpoint, form);
        toast.success("Created");
      }
      setDialogOpen(false);
      await loadItems();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${config.endpoint}/${id}`);
      toast.success("Deleted");
      await loadItems();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-muted-foreground text-sm"><span className="kpi-value">{items.length}</span> {config.label.toLowerCase()}</p>
        <Button onClick={openAdd} size="sm" data-testid={`add-${config.label.toLowerCase()}-button`} className="font-headline font-semibold">
          <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No {config.label.toLowerCase()} yet</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {config.columns.map(col => (
                  <TableHead key={col} className="text-muted-foreground text-xs uppercase">{col.replace(/_/g, " ")}</TableHead>
                ))}
                <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="border-border">
                  {config.columns.map(col => (
                    <TableCell key={col} className="text-sm">{item[col] || "—"}</TableCell>
                  ))}
                  <TableCell>
                    <Badge variant="outline" className={item.active !== false ? "bg-primary/20 text-primary border-primary/30 text-xs" : "bg-muted text-muted-foreground border-border text-xs"}>
                      {item.active !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {config.label === "Sites" && (
                      <Button variant="ghost" size="icon" onClick={() => setGeoSite(item)} title="Geofence" data-testid={`geofence-button-${item.id}`}>
                        <Navigation className={`h-3.5 w-3.5 ${item.geofence ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} {config.label.slice(0, -1)}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {config.fields.map(f => (
              <div key={f.key} className="space-y-2">
                <Label className="text-sm text-muted-foreground">{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="bg-background border-border"><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ""} className="bg-background border-border" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="font-headline font-semibold">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {geoSite && (
        <GeofenceDialog
          site={geoSite}
          open={!!geoSite}
          onClose={() => setGeoSite(null)}
          onSaved={loadItems}
        />
      )}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "contractor", contractor_id: "" });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [u, l] = await Promise.all([api.get("/admin/users"), api.get("/lookups")]);
      setUsers(u || []);
      setContractors(l.contractors || []);
    } catch (err) {
      toast.error("Failed to load users");
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditUser(null);
    setForm({ email: "", name: "", password: "", role: "contractor", contractor_id: "" });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email || "", name: u.name || "", password: "", role: u.role || "contractor", contractor_id: u.contractor_id || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.name) { toast.error("Email and name are required"); return; }
    if (!editUser && !form.password) { toast.error("Password is required for new users"); return; }
    setSaving(true);
    try {
      if (editUser) {
        const payload = { name: form.name, email: form.email, role: form.role, contractor_id: form.contractor_id };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editUser.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/auth/register", form);
        toast.success("User created");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-muted-foreground text-sm"><span className="kpi-value">{users.length}</span> users</p>
        <Button onClick={openAdd} size="sm" data-testid="add-user-button" className="font-headline font-semibold">
          <UserPlus className="h-4 w-4 mr-1" strokeWidth={2} /> Add User
        </Button>
      </div>

      {users.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No users</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-xs uppercase">Name</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Email</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Role</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      u.role === "admin" ? "bg-primary/20 text-primary border-primary/30 text-xs"
                        : u.role === "contractor" ? "bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs"
                          : "bg-muted text-muted-foreground border-border text-xs"
                    }>{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{contractors.find(c => c.id === u.contractor_id)?.name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-headline">{editUser ? "Edit" : "Create"} User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Name *</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Email *</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Password {editUser ? "(leave blank to keep)" : "*"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editUser ? "Leave blank to keep current" : "Password"} className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "contractor" && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Contractor Company</Label>
                <Select value={form.contractor_id} onValueChange={(v) => setForm({ ...form, contractor_id: v })}>
                  <SelectTrigger data-testid="user-contractor-select" className="bg-background border-border"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-submit" className="font-headline font-semibold">{saving ? "Saving..." : (editUser ? "Update" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotificationsTab() {
  const [recipients, setRecipients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const data = await api.get("/admin/notification-recipients");
      setRecipients(data || []);
    } catch (err) {
      toast.error("Failed to load recipients");
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => { setEditItem(null); setForm({ email: "", name: "" }); setDialogOpen(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ email: item.email, name: item.name || "" }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/admin/notification-recipients/${editItem.id}`, form);
        toast.success("Recipient updated");
      } else {
        await api.post("/admin/notification-recipients", form);
        toast.success("Recipient added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/notification-recipients/${id}`);
      toast.success("Recipient removed");
      await loadData();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const handleToggle = async (item) => {
    try {
      await api.put(`/admin/notification-recipients/${item.id}`, { active: !item.active });
      await loadData();
    } catch (err) { toast.error("Failed to update"); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api.post("/admin/notification-recipients/test");
      toast.success(`Test email sent to ${res.recipients.join(", ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test");
    } finally { setTesting(false); }
  };

  return (
    <div data-testid="notifications-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-muted-foreground">Email notifications are sent when a report is submitted.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-border" onClick={handleTest} disabled={testing || recipients.filter(r => r.active).length === 0} data-testid="test-notification-button">
            <Send className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {testing ? "Sending..." : "Send Test"}
          </Button>
          <Button size="sm" onClick={openAdd} data-testid="add-recipient-button" className="font-headline font-semibold">
            <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Add Recipient
          </Button>
        </div>
      </div>

      {recipients.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground text-sm">No notification recipients configured</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-xs uppercase">Name</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Email</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.map((r) => (
                <TableRow key={r.id} className="border-border" data-testid={`recipient-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.name || "-"}</TableCell>
                  <TableCell className="text-sm font-mono">{r.email}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`cursor-pointer ${r.active ? "bg-primary/20 text-primary border-primary/30" : "bg-muted text-muted-foreground border-border"}`}
                      onClick={() => handleToggle(r)}
                      data-testid={`toggle-recipient-${r.id}`}
                    >
                      {r.active ? "Active" : "Paused"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-recipient-${r.id}`}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} data-testid={`delete-recipient-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="recipient-dialog">
          <DialogHeader><DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Recipient</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Name</Label>
              <Input data-testid="recipient-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. BYK Operations" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Email *</Label>
              <Input data-testid="recipient-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="bg-background border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-recipient-button" className="font-headline font-semibold">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PowerBITab() {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    api.get("/admin/powerbi-key").then(res => { setApiKey(res.api_key || ""); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.put("/admin/powerbi-key");
      setApiKey(res.api_key);
      toast.success("API key regenerated");
    } catch (err) {
      toast.error("Failed to regenerate key");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const endpointUrl = `${backendUrl}/api/powerbi/reports?api_key=${apiKey}`;

  return (
    <div data-testid="powerbi-tab">
      <div className="space-y-6">
        <Card className="border-border bg-card">
          <CardContent className="py-6 space-y-4">
            <div>
              <h3 className="font-headline font-semibold text-sm mb-1">Power BI API Key</h3>
              <p className="text-xs text-muted-foreground mb-3">Use this key to authenticate Power BI Web connector requests.</p>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : (
                <div className="flex items-center gap-2">
                  <Input value={apiKey} readOnly className="bg-background border-border font-mono text-xs" data-testid="powerbi-api-key-input" />
                  <Button variant="outline" size="icon" onClick={() => handleCopy(apiKey)} className="border-border shrink-0" data-testid="copy-api-key-button">
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleRegenerate} disabled={regenerating} className="border-border shrink-0" data-testid="regenerate-api-key-button">
                    <RefreshCw className={`h-4 w-4 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  </Button>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-headline font-semibold text-sm mb-1">Endpoint URL</h3>
              <p className="text-xs text-muted-foreground mb-3">Use this URL in Power BI Desktop &gt; Get Data &gt; Web.</p>
              <div className="flex items-center gap-2">
                <Input value={endpointUrl} readOnly className="bg-background border-border font-mono text-xs" data-testid="powerbi-endpoint-input" />
                <Button variant="outline" size="icon" onClick={() => handleCopy(endpointUrl)} className="border-border shrink-0" data-testid="copy-endpoint-button">
                  <Copy className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            <div className="border border-border rounded-md p-4 bg-background">
              <h3 className="font-headline font-semibold text-sm mb-2">Quick Setup Guide</h3>
              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Open <strong>Power BI Desktop</strong></li>
                <li>Go to <strong>Home &gt; Get Data &gt; Web</strong></li>
                <li>Paste the <strong>Endpoint URL</strong> above</li>
                <li>Power BI will load the JSON data — expand the <code className="bg-card px-1 py-0.5 rounded text-primary">data</code> column</li>
                <li>Apply transforms as needed, then click <strong>Close &amp; Apply</strong></li>
                <li>Set up <strong>Scheduled Refresh</strong> in Power BI Service for automatic updates</li>
              </ol>
            </div>

            <div className="border border-border rounded-md p-4 bg-background">
              <h3 className="font-headline font-semibold text-sm mb-2">Optional Filters</h3>
              <p className="text-xs text-muted-foreground mb-2">Append query parameters to filter the data:</p>
              <div className="font-mono text-xs text-muted-foreground space-y-1">
                <p><code className="text-primary">date_from</code>=2026-01-01</p>
                <p><code className="text-primary">date_to</code>=2026-01-31</p>
                <p><code className="text-primary">site_id</code>=&lt;site uuid&gt;</p>
                <p><code className="text-primary">contractor_id</code>=&lt;contractor uuid&gt;</p>
                <p><code className="text-primary">shift</code>=Day or Night</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <Badge variant="outline" className="bg-primary/20 text-primary border-primary/30 text-xs ml-2">Admin</Badge>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-headline text-3xl font-bold tracking-tight mb-2">Admin Panel</h1>
        <div className="flex items-center justify-between mb-8">
          <p className="text-muted-foreground text-sm">Manage lookup data and system users</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/reports")} data-testid="view-reports-button">
              <FileText className="h-4 w-4 mr-1" strokeWidth={1.5} /> View Reports
            </Button>
            <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/analytics")} data-testid="analytics-button">
              <BarChart3 className="h-4 w-4 mr-1" strokeWidth={1.5} /> Analytics Dashboard
            </Button>
          </div>
        </div>

        <Tabs defaultValue="sites" className="w-full">
          <TabsList className="bg-card border border-border mb-6 flex-wrap h-auto gap-1 p-1">
            {Object.entries(TAB_CONFIG).map(([key, cfg]) => (
              <TabsTrigger key={key} value={key} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid={`admin-tab-${key}`}>
                <cfg.icon className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />{cfg.label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-users">
              <Users className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Users
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-notifications">
              <Bell className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Notifications
            </TabsTrigger>
            <TabsTrigger value="powerbi" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-powerbi">
              <Key className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Power BI
            </TabsTrigger>
          </TabsList>

          {Object.entries(TAB_CONFIG).map(([key, cfg]) => (
            <TabsContent key={key} value={key}>
              <LookupTab config={cfg} />
            </TabsContent>
          ))}
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          <TabsContent value="notifications">
            <NotificationsTab />
          </TabsContent>
          <TabsContent value="powerbi">
            <PowerBITab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
